import { useRef, useEffect, useState, useCallback } from 'react';
import {
  forceSimulation,
  forceLink,
  forceManyBody,
  forceCenter,
  forceCollide,
  type SimulationNodeDatum,
  type SimulationLinkDatum,
} from 'd3-force';
import { select } from 'd3-selection';
import { zoom as d3Zoom, zoomIdentity } from 'd3-zoom';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GraphNode {
  id: string;
  type: 'contact' | 'company';
  label: string;
  subtype?: string;
  interactions?: number;
}

export interface GraphEdge {
  source: string;
  target: string;
  type: string;
  weight?: number;
}

export interface ContextGraphProps {
  nodes: GraphNode[];
  edges: GraphEdge[];
  selectedId?: string;
  onSelect: (id: string, type: 'contact' | 'company') => void;
}

// ---------------------------------------------------------------------------
// Simulation datum types (d3 mutates nodes/links with x, y, etc.)
// ---------------------------------------------------------------------------

interface SimNode extends SimulationNodeDatum {
  id: string;
  type: 'contact' | 'company';
  label: string;
  subtype?: string;
  interactions?: number;
}

interface SimLink extends SimulationLinkDatum<SimNode> {
  type: string;
  weight?: number;
}

// ---------------------------------------------------------------------------
// Color maps
// ---------------------------------------------------------------------------

const contactColors: Record<string, string> = {
  customer: '#34d399', // emerald-400
  lead: '#60a5fa',     // blue-400
  partner: '#a78bfa',  // purple-400
  vendor: '#fbbf24',   // amber-400
  internal: '#94a3b8',  // slate-400 (surface)
  other: '#9ca3af',    // gray-400
};

const edgeColors: Record<string, string> = {
  works_at: '#60a5fa',
  manages: '#a78bfa',
  referred_by: '#34d399',
  partner_of: '#fbbf24',
  vendor_for: '#fb923c',
  customer_of: '#34d399',
  related_to: '#64748b',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getNodeColor(node: SimNode): string {
  if (node.type === 'company') return '#3b82f6'; // blue-500
  return contactColors[node.subtype ?? 'other'] ?? '#9ca3af';
}

function getNodeRadius(node: SimNode): number {
  if (node.type === 'company') return 28;
  const base = 18;
  const extra = Math.min((node.interactions ?? 0) / 10, 8);
  return base + extra;
}

function getInitials(label: string): string {
  const parts = label.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return label.slice(0, 2).toUpperCase();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ContextGraph({ nodes, edges, selectedId, onSelect }: ContextGraphProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const gRef = useRef<SVGGElement>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; text: string } | null>(null);
  const simulationRef = useRef<ReturnType<typeof forceSimulation<SimNode>> | null>(null);

  // Stable callback refs to avoid recreating simulation on every render
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;

  const handleNodeClick = useCallback((node: SimNode) => {
    onSelectRef.current(node.id, node.type);
  }, []);

  // -------------------------------------------------------------------------
  // Build / update simulation
  // -------------------------------------------------------------------------
  useEffect(() => {
    const svg = svgRef.current;
    const g = gRef.current;
    if (!svg || !g) return;

    const width = svg.clientWidth || 800;
    const height = svg.clientHeight || 500;

    // Deep-copy nodes & edges so d3 can mutate them
    const simNodes: SimNode[] = nodes.map((n) => ({ ...n }));
    const simLinks: SimLink[] = edges
      .filter((e) => {
        const hasSource = simNodes.some((n) => n.id === e.source);
        const hasTarget = simNodes.some((n) => n.id === e.target);
        return hasSource && hasTarget;
      })
      .map((e) => ({
        source: e.source,
        target: e.target,
        type: e.type,
        weight: e.weight,
      }));

    // Clear previous content
    const gSel = select(g);
    gSel.selectAll('*').remove();

    // Edge group
    const linkGroup = gSel.append('g').attr('class', 'links');
    const linkSel = linkGroup
      .selectAll('line')
      .data(simLinks)
      .join('line')
      .attr('stroke', (d) => edgeColors[d.type] ?? '#475569')
      .attr('stroke-width', (d) => Math.max(1, (d.weight ?? 1) * 0.8))
      .attr('stroke-opacity', 0.45);

    // Edge labels
    const edgeLabelSel = linkGroup
      .selectAll('text')
      .data(simLinks)
      .join('text')
      .attr('fill', '#64748b')
      .attr('font-size', '9')
      .attr('text-anchor', 'middle')
      .attr('dy', -4)
      .text((d) => d.type.replace(/_/g, ' '));

    // Node group
    const nodeGroup = gSel.append('g').attr('class', 'nodes');
    const nodeSel = nodeGroup
      .selectAll<SVGGElement, SimNode>('g')
      .data(simNodes, (d) => d.id)
      .join('g')
      .attr('cursor', 'pointer')
      .on('click', (_event, d) => handleNodeClick(d))
      .on('mouseenter', (event, d) => {
        const rect = svg.getBoundingClientRect();
        setTooltip({
          x: event.clientX - rect.left,
          y: event.clientY - rect.top - 12,
          text: `${d.label} (${d.subtype ?? d.type})`,
        });
      })
      .on('mouseleave', () => setTooltip(null));

    // Node circles
    nodeSel
      .append('circle')
      .attr('r', (d) => getNodeRadius(d))
      .attr('fill', (d) => getNodeColor(d))
      .attr('fill-opacity', 0.15)
      .attr('stroke', (d) => getNodeColor(d))
      .attr('stroke-width', 2);

    // Node initials
    nodeSel
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .attr('fill', (d) => getNodeColor(d))
      .attr('font-size', (d) => (d.type === 'company' ? '12' : '10'))
      .attr('font-weight', '600')
      .text((d) => getInitials(d.label));

    // Drag behavior
    let dragTarget: SimNode | null = null;
    let dragOffset = { x: 0, y: 0 };

    nodeSel
      .on('mousedown', (event, d) => {
        event.stopPropagation();
        dragTarget = d;
        dragOffset = { x: event.offsetX - (d.x ?? 0), y: event.offsetY - (d.y ?? 0) };
        d.fx = d.x;
        d.fy = d.y;
        simulation.alphaTarget(0.3).restart();
      });

    select(svg)
      .on('mousemove.drag', (event) => {
        if (!dragTarget) return;
        // Account for current zoom transform
        const transform = select(g).attr('transform');
        let tx = 0, ty = 0, s = 1;
        if (transform) {
          const tMatch = transform.match(/translate\(([^,]+),([^)]+)\)/);
          const sMatch = transform.match(/scale\(([^)]+)\)/);
          if (tMatch) { tx = parseFloat(tMatch[1]); ty = parseFloat(tMatch[2]); }
          if (sMatch) { s = parseFloat(sMatch[1]); }
        }
        dragTarget.fx = (event.offsetX - tx) / s;
        dragTarget.fy = (event.offsetY - ty) / s;
      })
      .on('mouseup.drag', () => {
        if (dragTarget) {
          dragTarget.fx = null;
          dragTarget.fy = null;
          simulation.alphaTarget(0);
          dragTarget = null;
        }
      });

    // Simulation
    const simulation = forceSimulation<SimNode>(simNodes)
      .force(
        'link',
        forceLink<SimNode, SimLink>(simLinks)
          .id((d) => d.id)
          .distance(120)
          .strength(0.6),
      )
      .force('charge', forceManyBody().strength(-300))
      .force('center', forceCenter(width / 2, height / 2))
      .force('collide', forceCollide<SimNode>().radius((d) => getNodeRadius(d) + 8))
      .on('tick', () => {
        linkSel
          .attr('x1', (d) => (d.source as SimNode).x ?? 0)
          .attr('y1', (d) => (d.source as SimNode).y ?? 0)
          .attr('x2', (d) => (d.target as SimNode).x ?? 0)
          .attr('y2', (d) => (d.target as SimNode).y ?? 0);

        edgeLabelSel
          .attr('x', (d) => (((d.source as SimNode).x ?? 0) + ((d.target as SimNode).x ?? 0)) / 2)
          .attr('y', (d) => (((d.source as SimNode).y ?? 0) + ((d.target as SimNode).y ?? 0)) / 2);

        nodeSel.attr('transform', (d) => `translate(${d.x ?? 0},${d.y ?? 0})`);
      });

    simulationRef.current = simulation;

    // Zoom
    const zoomBehavior = d3Zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.2, 4])
      .on('zoom', (event) => {
        const { x, y, k } = event.transform;
        select(g).attr('transform', `translate(${x},${y}) scale(${k})`);
      });
    select(svg).call(zoomBehavior).call(zoomBehavior.transform, zoomIdentity);

    return () => {
      simulation.stop();
      select(svg).on('.zoom', null).on('mousemove.drag', null).on('mouseup.drag', null);
    };
    // Re-run when nodes/edges identity changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, edges, handleNodeClick]);

  // -------------------------------------------------------------------------
  // Update selected highlight without rebuilding the simulation
  // -------------------------------------------------------------------------
  useEffect(() => {
    const g = gRef.current;
    if (!g) return;
    const gSel = select(g);
    gSel.selectAll<SVGCircleElement, SimNode>('.nodes circle')
      .attr('stroke-width', (d) => (d.id === selectedId ? 3.5 : 2))
      .attr('filter', (d) => (d.id === selectedId ? 'drop-shadow(0 0 6px rgba(99,102,241,0.6))' : 'none'));
  }, [selectedId]);

  return (
    <div className="relative w-full h-full min-h-[400px] bg-surface-900 rounded-lg border border-surface-700 overflow-hidden">
      <svg ref={svgRef} className="w-full h-full" style={{ minHeight: '400px' }}>
        <g ref={gRef} />
      </svg>
      {/* Tooltip */}
      {tooltip && (
        <div
          className="absolute pointer-events-none bg-surface-800 border border-surface-600 rounded px-2 py-1 text-xs text-surface-200 shadow-lg z-50"
          style={{ left: tooltip.x, top: tooltip.y, transform: 'translate(-50%, -100%)' }}
        >
          {tooltip.text}
        </div>
      )}
      {/* Legend */}
      <div className="absolute bottom-3 left-3 flex flex-wrap gap-3 bg-surface-900/80 backdrop-blur-sm rounded-lg px-3 py-2 border border-surface-700/50 text-[10px]">
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-full bg-blue-500 inline-block" />
          Company
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 inline-block" />
          Customer
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-full bg-blue-400 inline-block" />
          Lead
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-full bg-purple-400 inline-block" />
          Partner
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-full bg-amber-400 inline-block" />
          Vendor
        </span>
      </div>
    </div>
  );
}
